import { useMemo, useState } from "react";
import { Alert, Button, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate } from "@fortawesome/free-solid-svg-icons";
import { streamingChatDeliveryDescription } from "../utils/streamingLoadTestOutcome.js";
import { streamingTestFeedback } from "../utils/loadTestFeedback.js";
import useLoadTestFlow from "../hooks/useLoadTestFlow.js";
import {
    LoadTestFlowModal, LoadTestNotice, LoadTestBotCount, LoadTestSetupSummary,
    LoadTestProgress, LoadTestResult, LoadTestComparisonTable, LoadTestMetricCard as MetricCard,
} from "./LoadTestFlow.js";
import {
    cancelStreamingLoadTest,
    getStreamingLoadTestReport,
    getStreamingLoadTestStatus,
    startStreamingLoadTest,
    streamingLoadTestComparisonStatus,
} from "../api/streamingLoadTest.js";

const DEFAULT_CONNECTION_COUNT = 100;
const MIN_CONNECTION_COUNT = 2;
const MAX_CONNECTION_COUNT = 2000;
const FIXED_RUN_DURATION_SECONDS = 600;

const formatDuration = (seconds) => {
    const value = Number(seconds);
    if (value >= 60 && value % 60 === 0) return `${value / 60}min`;
    return `${value}s`;
};

const PHASE_LABELS = Object.freeze({
    reserved: "Reserva criada",
    preparing: "Preparando conexões",
    ramp_up: "Aumentando a carga",
    soak: "Mantendo a carga",
    report_contract_warmup: "Aquecendo snapshots",
    report_contract: "Validando conexões",
    report_contract_page_leave: "Validando saída da página",
    report_contract_page_rejoin: "Validando retorno da página",
    report_contract_reconnect: "Validando reconexão",
    report_contract_steady: "Mantendo público estável",
    report_contract_terminal_zero: "Confirmando zero conexões",
    cool_down: "Reduzindo a carga",
    settling: "Consolidando métricas",
    reporting: "Gerando relatório",
    completed: "Concluído",
    cancelling: "Encerrando",
    cancelled: "Encerrado",
    failed: "Falhou",
});

const COMPARISON_LABELS = Object.freeze({
    audience_sessions: "Sessões de audiência",
    registered_sessions: "Sessões registradas",
    guest_sessions: "Sessões visitantes",
    unique_viewers: "Pessoas únicas",
    registered_unique_viewers: "Participantes únicos",
    guest_unique_viewers: "Visitantes únicos",
    peak_viewers: "Pico simultâneo",
    start_viewers: "Conexões no início",
    end_viewers: "Conexões no fim",
    average_viewers: "Média ponderada",
    viewer_snapshot_provenance: "Snapshots atuais",
    viewer_terminal_zero: "Zero no encerramento",
    viewer_reconnect_grace: "Reconexão na janela de graça",
    reaction_count: "Reações",
    chat_messages: "Mensagens no chat",
    poll_votes: "Votos",
});

const createDefaultSeed = () => `painel-stream-${new Date().toISOString().replace(/[^0-9]/g, "").slice(0, 14)}`;

const orchestrationFor = (value, durationValue = FIXED_RUN_DURATION_SECONDS) => {
    const connections = Number(value);
    const defaultPhase = connections <= 10
        ? { rampSeconds: 30, soakSeconds: 120, cooldownSeconds: 30, estimatedMinutes: [3, 5] }
        : connections <= 100
            ? { rampSeconds: 60, soakSeconds: 300, cooldownSeconds: 60, estimatedMinutes: [7, 10] }
            : { rampSeconds: 120, soakSeconds: 600, cooldownSeconds: 120, estimatedMinutes: [14, 18] };
    const soakSeconds = Number.isInteger(Number(durationValue)) && Number(durationValue) > 0
        ? Number(durationValue)
        : FIXED_RUN_DURATION_SECONDS;
    const totalMinutes = Math.max(1, Math.ceil((defaultPhase.rampSeconds + soakSeconds + defaultPhase.cooldownSeconds) / 60));
    const defaultTotalMinutes = Math.ceil((defaultPhase.rampSeconds + defaultPhase.soakSeconds + defaultPhase.cooldownSeconds) / 60);
    const etaUpperBound = totalMinutes + (defaultPhase.estimatedMinutes[1] - defaultTotalMinutes);
    return {
        ramp: formatDuration(defaultPhase.rampSeconds),
        soak: formatDuration(soakSeconds),
        cooldown: formatDuration(defaultPhase.cooldownSeconds),
        eta: `${totalMinutes} a ${etaUpperBound} minutos`,
    };
};

const loadTestSessionKey = ({ eventId, scheduleId, streamingId }) => (
    `streaming-load-test:${eventId}:${scheduleId}:${streamingId}`
);

const compactLoadTestReport = (report) => {
    if (!report || typeof report !== "object") return {};
    const visibleFields = [
        "passed",
        "validation",
        "chat_delivery_validation",
        "partial",
        "scenario",
        "hls_validation",
        "identity",
        "warnings",
        "ready_bots",
        "local_peak_stream_viewers",
        "acknowledgement_latency",
        "fanout",
        "actions",
        "checks",
        "durable_interaction_acknowledgements",
        "guest_beacon_containment",
    ];
    const compactReport = visibleFields.reduce((result, field) => {
        if (report[field] !== undefined) result[field] = report[field];
        return result;
    }, {});
    if (report.failure !== undefined) {
        compactReport.failure = typeof report.failure === "string"
            ? report.failure
            : report.failure?.message ? { message: report.failure.message } : undefined;
    }
    return compactReport;
};

const compactPlatformReport = (report) => {
    if (!report || typeof report !== "object") return null;
    const copyFields = (source, fields) => Object.fromEntries(fields
        .filter((field) => typeof source?.[field] === "boolean"
            || (typeof source?.[field] === "number" && Number.isFinite(source[field])))
        .map((field) => [field, source[field]]));
    return {
        ...(report.captured_at ? { captured_at: report.captured_at } : {}),
        metrics: copyFields(report.metrics, [
            "audience_sessions",
            "registered_sessions",
            "guest_sessions",
            "unique_viewers",
            "registered_unique_viewers",
            "guest_unique_viewers",
            "reaction_count",
            "chat_messages",
            "poll_votes",
        ]),
        viewer_summary: copyFields(report.viewer_summary, [
            "start_viewers",
            "end_viewers",
            "peak_viewers",
            "average_viewers",
        ]),
        viewer_integrity: copyFields(report.viewer_integrity, [
            "snapshot_points",
            "current_snapshot_points",
            "current_snapshot_provenance",
            "viewer_count_weighted_milliseconds",
            "viewer_duration_milliseconds",
        ]),
    };
};

const buildStoredLoadTestReport = (data) => {
    const job = data?.job || {};
    const report = compactLoadTestReport(data?.report || job.result || {});
    return {
        job: {
            id: job.id,
            status: job.status,
            progress: job.progress,
            phase: job.phase,
            phase_message: job.phase_message,
            created_at: job.created_at,
            started_at: job.started_at,
            finished_at: job.finished_at,
            error: job.error,
            input: job.input ? {
                connection_count: job.input.connection_count,
                run_duration_seconds: job.input.run_duration_seconds,
                random_seed: job.input.random_seed,
                scenario: job.input.scenario,
                profile: job.input.profile,
            } : undefined,
            metadata: job.metadata ? {
                ready: job.metadata.ready,
                active: job.metadata.active,
                identity: job.metadata.identity,
                hls_validation: job.metadata.hls_validation,
            } : undefined,
        },
        report,
        platform_report: compactPlatformReport(data?.platform_report),
        validation: data?.validation ? {
            diagnostic_only: data.validation.diagnostic_only === true,
            passed: typeof data.validation.passed === "boolean" ? data.validation.passed : null,
        } : null,
        test_period: data?.test_period ? {
            start_at: data.test_period.start_at || null,
            end_at: data.test_period.end_at || null,
        } : null,
        comparison: data?.comparison ? {
            diagnostic_only: data.comparison.diagnostic_only === true,
            passed: data.comparison.passed,
            inconclusive: data.comparison.inconclusive,
            inconclusive_reason: data.comparison.inconclusive_reason,
            unfinished_tester_sessions: data.comparison.unfinished_tester_sessions,
            comparisons: data.comparison.comparisons,
        } : null,
    };
};

const formatNumber = (value) => Number(value || 0).toLocaleString("pt-BR");
const valueOrDash = (value) => value === null || value === undefined ? "—" : formatNumber(value);
const expectedComparisonValue = (row) => {
    if (row?.expected_min !== null && row?.expected_min !== undefined && row?.expected_max !== null && row?.expected_max !== undefined && Number(row.expected_min) !== Number(row.expected_max)) {
        return `${formatNumber(row.expected_min)}–${formatNumber(row.expected_max)}`;
    }
    return valueOrDash(row?.expected);
};

export const StreamingLoadTestModal = ({
    show,
    onHide,
    onTestRunningChange = () => {},
    onTerminal = () => {},
    context = {},
}) => {
    const [connectionCount, setConnectionCount] = useState(String(DEFAULT_CONNECTION_COUNT));
    const [randomSeed, setRandomSeed] = useState(createDefaultSeed);
    const [scenario, setScenario] = useState("autonomous");
    const identity = useMemo(() => ({
        eventId: Number(context.eventId),
        scheduleId: Number(context.scheduleId),
        streamingId: Number(context.streamingId),
    }), [context.eventId, context.scheduleId, context.streamingId]);
    const sessionKey = useMemo(() => loadTestSessionKey(identity), [identity]);
    const flow = useLoadTestFlow({
        show, sessionKey, enabled: Boolean(identity.eventId && identity.scheduleId && identity.streamingId),
        getStatus: (jobId) => getStreamingLoadTestStatus({ jobId, ...identity }),
        getReport: (jobId) => getStreamingLoadTestReport({ jobId, ...identity }),
        cancelJob: (jobId) => cancelStreamingLoadTest({ jobId, ...identity }),
        startJob: (input) => startStreamingLoadTest({ ...identity, ...input }),
        compactReport: buildStoredLoadTestReport,
        getOutcome: streamingTestFeedback,
        onTestRunningChange, onTerminal,
        onRestoreInputs: (nextJob) => {
            setConnectionCount(String(nextJob.input?.connection_count || connectionCount || DEFAULT_CONNECTION_COUNT));
            setRandomSeed(String(nextJob.input?.random_seed || randomSeed || createDefaultSeed()));
            setScenario(String(nextJob.input?.scenario || "autonomous"));
        },
    });
    const { job, finalData, submitting } = flow;
    const targetConnections = Number(job?.input?.connection_count || connectionCount || DEFAULT_CONNECTION_COUNT);
    const orchestration = orchestrationFor(targetConnections, job?.input?.run_duration_seconds || FIXED_RUN_DURATION_SECONDS);
    const start = () => {
        const count = Number(connectionCount);
        const seed = String(randomSeed || "").trim();
        if (!Number.isInteger(count) || count < MIN_CONNECTION_COUNT || count > MAX_CONNECTION_COUNT) {
            flow.setError("Informe um número inteiro entre " + MIN_CONNECTION_COUNT + " e " + MAX_CONNECTION_COUNT.toLocaleString("pt-BR") + " bots.");
            return;
        }
        if (!seed || seed.length > 200) {
            flow.setError("Informe uma semente entre 1 e 200 caracteres.");
            return;
        }
        flow.start({ connectionCount: count, runDurationSeconds: FIXED_RUN_DURATION_SECONDS, randomSeed: seed,
            scenario, streamCohortsPreset: scenario === "autonomous" ? "realistic" : "contract" });
    };
    const reset = () => {
        if (!flow.reset()) return;
        setConnectionCount(String(DEFAULT_CONNECTION_COUNT));
        setRandomSeed(createDefaultSeed());
        setScenario("autonomous");
    };

    const result = finalData?.report || job?.result || {};
    const runnerFailed = job?.status === "failed";
    const runnerIncomplete = job?.status !== "completed" || result.partial === true;
    const hasComparisonRows = Array.isArray(finalData?.comparison?.comparisons)
        && finalData.comparison.comparisons.length > 0;
    const comparisonIsDiagnostic = finalData?.validation?.diagnostic_only === true
        || finalData?.comparison?.diagnostic_only === true
        || (runnerFailed && result.partial !== true && hasComparisonRows);
    const comparisonStatus = comparisonIsDiagnostic && hasComparisonRows
        ? "diagnostic"
        : runnerIncomplete ? "unavailable" : streamingLoadTestComparisonStatus(finalData?.comparison);
    const comparison = comparisonStatus === "unavailable" ? [] : finalData?.comparison?.comparisons || [];
    const reportContractScenario = String(result?.scenario || job?.input?.scenario || "").toLowerCase() === "report_contract";
    const hls = result?.hls_validation || job?.metadata?.hls_validation || null;
    const identityResult = result?.identity || job?.metadata?.identity || {};
    const runnerFailureMessage = typeof result.failure === "string" ? result.failure : result.failure?.message || job?.error;
    const ackP95Values = Object.values(result?.acknowledgement_latency || {})
        .map((entry) => Number(entry?.p95Ms))
        .filter(Number.isFinite);
    const maximumAckP95 = ackP95Values.length ? Math.max(...ackP95Values) : null;
    const fanout = result?.fanout || {};
    const chatCoverage = result?.chat_delivery_validation?.live_delivery?.coverage_percent ?? fanout.reliable_chat_percent;
    const testOutcome = streamingTestFeedback({ ...finalData, job: finalData?.job || job, report: result });
    const chatDeliveryDescription = streamingChatDeliveryDescription(result);
    const unfinishedTesterSessions = Number(finalData?.comparison?.unfinished_tester_sessions || 0);
    const comparisonBadgeText = comparisonStatus === "diagnostic"
        ? "Diagnóstico"
        : comparisonStatus === "matched"
        ? "Corresponde"
        : comparisonStatus === "mismatch"
            ? "Divergente"
            : !reportContractScenario && runnerIncomplete ? "Não executada" : "Indisponível";
    const acceptedActions = Object.values(result?.actions || {}).reduce(
        (total, action) => total + Number(action?.accepted || 0),
        0,
    );
    return <LoadTestFlowModal
        show={show} onHide={onHide} flow={flow} testLabel="Transmissão" onStart={start} onReset={reset}
        cancelDescription="As conexões serão encerradas de forma gradual e um resultado parcial será gerado. A transmissão continuará ao vivo e os dados já criados serão mantidos."
        configuration={<>
            <LoadTestNotice>
                O resultado considera apenas o período deste teste. Os bots simulam espectadores com chat, reações e votos. A transmissão, o chat e a enquete permanecem inalterados.
            </LoadTestNotice>
            <LoadTestBotCount value={connectionCount} onChange={setConnectionCount} minimum={MIN_CONNECTION_COUNT} maximum={MAX_CONNECTION_COUNT} />
            <LoadTestSetupSummary items={[
                ["Bots configurados", formatNumber(connectionCount)], ["Duração ativa", "10min (600s)"],
                ["Carga estável", orchestration.soak], ["Aumento / Redução", orchestration.ramp + " / " + orchestration.cooldown],
                ["Duração total estimada", orchestration.eta],
            ]} covered="chat em português, reações, votos em enquetes ativas e diferentes perfis de espectadores."
                excluded="alterações nas configurações da live e nas enquetes já encerradas." />
            <Form.Group className="mb-3">
                <Form.Label>Tempo ativo dos bots (segundos)</Form.Label>
                <Form.Control
                    type="number"
                    value={FIXED_RUN_DURATION_SECONDS}
                    disabled
                    readOnly
                />
                <Form.Text className="text-muted mt-2 d-block form-text">Duração fixa: 600 segundos (10 minutos).</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>Semente determinística</Form.Label>
                <div className="d-flex gap-2">
                    <Form.Control
                        type="text"
                        maxLength={200}
                        value={randomSeed}
                        onChange={(event) => setRandomSeed(event.target.value)}
                        isInvalid={randomSeed !== "" && (!String(randomSeed).trim() || String(randomSeed).trim().length > 200)}
                        disabled={submitting}
                    />
                    <Button type="button" variant="outline-secondary" title="Gerar outra semente" onClick={() => setRandomSeed(createDefaultSeed())} disabled={submitting}>
                        <FontAwesomeIcon icon={faRotate} />
                    </Button>
                </div>
                <Form.Control.Feedback type="invalid">Informe uma semente entre 1 e 200 caracteres.</Form.Control.Feedback>
                <Form.Text className="text-muted mt-2 d-block form-text">Identificador gerado automaticamente. A mesma semente permite repetir os perfis e o roteiro de ações dos bots para comparar execuções.</Form.Text>
            </Form.Group>
        </>}
        progress={<LoadTestProgress flow={flow} phaseLabels={PHASE_LABELS} targetConnections={targetConnections} estimate={orchestration.eta} />}
        results={finalData && <LoadTestResult outcome={testOutcome} partial={result.partial || testOutcome.cancelled} comparisonLabel={comparisonBadgeText} description={chatDeliveryDescription}>
            {unfinishedTesterSessions > 0 && (
                <Alert variant="primary" className="p-2 d-block">
                    {formatNumber(unfinishedTesterSessions)} {unfinishedTesterSessions === 1 ? "sessão de bot está" : "sessões de bots estão"} sem horário de saída gravado. Isso indica uma falha de registro, não conexões abertas agora.
                </Alert>
            )}
            {runnerFailed && runnerFailureMessage && (
                <Alert variant="danger" className="p-2 d-block">Falha do runner: {runnerFailureMessage}</Alert>
            )}
            <Row className="g-2 mb-3">
                <MetricCard title="Participantes autenticados" value={formatNumber(identityResult.participant_authenticated)} subtitle={`${formatNumber(identityResult.participant_replaced)} substituídos por visitantes`} md={4} xl={4} />
                <MetricCard title="Visitantes autenticados" value={formatNumber(identityResult.visitor_authenticated)} subtitle={`${formatNumber(result.ready_bots)} conexões prontas`} md={4} xl={4} />
                <MetricCard title="Pico local" value={formatNumber(result.local_peak_stream_viewers)} subtitle={`Seed: ${job?.input?.random_seed || "automática"}`} md={4} xl={4} />
                <MetricCard title="Maior ACK p95" value={maximumAckP95 === null ? "—" : `${maximumAckP95.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} ms`} subtitle={`${formatNumber(acceptedActions)} ações aceitas`} md={4} xl={4} />
                <MetricCard title="Entrega do chat ao vivo" value={chatCoverage == null ? "—" : `${Number(chatCoverage).toLocaleString("pt-BR", { maximumFractionDigits: 6 })}%`} subtitle={Number.isFinite(result?.chat_delivery_validation?.minimum_coverage_percent) ? `Meta mínima: ${formatNumber(result.chat_delivery_validation.minimum_coverage_percent)}%` : "Por conexão elegível"} md={4} xl={4} />
                <MetricCard
                    title="Validação HLS"
                    value={reportContractScenario ? "Não aplicável" : hls?.status === "passed" ? "Reprodução validada" : "Aviso"}
                    subtitle={reportContractScenario
                        ? "O contrato valida somente Socket.IO; nenhum navegador ou player é usado."
                        : hls
                        ? `Início: ${valueOrDash(hls.startup_ms)} ms · Progresso: ${valueOrDash(hls.progressed_seconds)}s · ${hls.resolution?.width || 0}×${hls.resolution?.height || 0}`
                        : "Sem metadados do player"}
                    md={4}
                    xl={4}
                />
            </Row>

            <h6 className="pt-2 mb-2">Comparação de dados{comparisonStatus === "diagnostic" ? " (diagnóstico)" : ""}</h6>
            {comparisonStatus === "diagnostic" && (
                <div className="small text-muted mb-2">
                    Esta comparação do relatório é informativa e não altera a aprovação do runner.
                </div>
            )}
            <LoadTestComparisonTable rows={comparison.map((row) => ({
                id: row.metric,
                label: COMPARISON_LABELS[row.metric] || row.metric,
                expected: expectedComparisonValue(row),
                observed: valueOrDash(row.observed),
                difference: valueOrDash(row.delta),
                status: row.matches === true ? "Corresponde" : row.matches === false ? "Divergente" : "Indisponível",
                variant: row.matches == null ? "secondary" : "primary",
                tone: row.matches === false ? "table-warning" : "",
            }))} />
        </LoadTestResult>}
    />;
};
